import * as THREE from 'three';
/* ============================================================================
   surfaces.js — material + surface-fidelity layer for AGENTSEE (three 0.170)

   The floor is warm, dark and lamp-lit: ACESFilmic at exposure 1.5, HDRI
   environment at 0.11, a 3.1 warm key and a dozen ~0xffc79a point lamps.
   Every default in here is tuned for THAT room. Values that look flat in a
   daylight scene are correct here — a lamp-lit interior lives in the bottom
   two stops, so contrast comes from roughness break-up and contact occlusion,
   not from brightening.

   Nothing in this module loads a texture off disk. grime() is pure shader,
   contactShadow() paints one 128px canvas, the rest re-tunes materials the
   caller already built with M() / pbrMat().

   DRAW CALLS — where the caller should merge instead:
     · floorMesh[*] is 14 separate parquet patches → 14 draws + 14 material
       uploads. One shared upgradeParquet() material across all of them costs
       one program; merging the 12 parquet boxes into a single BufferGeometry
       with world-scaled UVs would take it to one draw.
     · groundAll(objs) is one draw per blob. groundAll(objs, {batch:true}) is
       ONE draw for the lot — use it for anything that never moves.
     · The book InstancedMesh in main.js is already the right pattern; the
       ~40 B() calls that share WOODD are not (they could be merged per room).
   ============================================================================ */

/* ---------- one-time state ---------- */
let MAX_ANISO = 8;                       // overwritten by initSurfaces(renderer)
let BLOB_GEO = null, BLOB_MAT = null, BLOB_TEX = null, BATCH_MAT = null;
const GRIME_CACHE = new Map();           // srcUuid|opts -> grimed clone (one program per variant)
const SCREEN_MATS = [];                  // materials whose rolling glow needs a clock

/* raise every map on a material to the GPU's real anisotropy ceiling.
   pbrMat() in main.js hardcodes 8; on Apple silicon the ceiling is 16. */
const MAP_KEYS = ['map','normalMap','roughnessMap','metalnessMap','aoMap','emissiveMap',
                  'clearcoatMap','clearcoatRoughnessMap','clearcoatNormalMap','sheenColorMap','alphaMap'];
function sharpen(mat){
  for(const k of MAP_KEYS){
    const t = mat[k];
    if(t && t.anisotropy !== MAX_ANISO){ t.anisotropy = MAX_ANISO; t.needsUpdate = true; }
  }
  return mat;
}

/* Material.copy() does NOT carry onBeforeCompile or customProgramCacheKey, so a
   grimed material silently loses its grime when converted to physical. Carry
   them by hand on every conversion in this file. */
function carryShaderHooks(dst, src){
  if(src.onBeforeCompile && src.onBeforeCompile !== THREE.Material.prototype.onBeforeCompile)
    dst.onBeforeCompile = src.onBeforeCompile;
  if(src.customProgramCacheKey && src.userData && src.userData.__grimeKey){
    dst.userData.__grimeKey = src.userData.__grimeKey;
    dst.customProgramCacheKey = () => src.userData.__grimeKey;
  }
  return dst;
}

/* ---------- initSurfaces ---------- */
/* Call once, right after the renderer exists and BEFORE the first surfaces call.
   Caches the GPU anisotropy ceiling and pre-builds the shared contact-shadow
   geometry/texture so the first grounded prop doesn't stall a frame. */
export function initSurfaces(renderer, opts = {}){
  if(renderer && renderer.capabilities && renderer.capabilities.getMaxAnisotropy)
    MAX_ANISO = Math.max(1, Math.min(16, renderer.capabilities.getMaxAnisotropy()));
  blobGeo(); blobTex();
  /* the contact-shadow material is shared, so its opacity is global. Set it here
     if 0.46 isn't right for you — the first contactShadow() call locks it. */
  if(opts.shadowOpacity !== undefined) blobMat(opts.shadowOpacity);
  return { maxAnisotropy: MAX_ANISO };
}

/* ============================================================================
   grime — procedural roughness break-up + crevice darkening, no new textures
   ============================================================================ */
/* Injects a 3-octave value-noise field in WORLD space (so it doesn't stretch
   with pbrMat's repeat, and doesn't swim when a prop is scaled) and uses it to
   (a) push roughnessFactor either side of the material's value, and
   (b) darken diffuse where the field bottoms out — reads as dirt in corners,
   scuffing along skirtings and the grease that collects on shared surfaces.
   A second term biases grime toward the floor: an office is dirtiest below
   knee height, which is exactly where the eye looks for wear.

   SHARED MATERIALS: main.js caches every M() material in MATS and hands the
   same instance to dozens of meshes. grime() therefore CLONES by default —
   the returned material is a new instance (textures are shared by reference,
   so the clone is ~free) and the caller must assign it. The clone is cached on
   (source uuid + options), so grime(WOODD) called 40 times returns the SAME
   clone and compiles ONE program. Pass {inPlace:true} only when you own the
   material outright (e.g. a material you just constructed).

   opts: scale    world units per noise cell — smaller = finer grain  (default 1.35)
         strength 0..1, drives both roughness swing and crevice darkening (0.34)
         seed     decorrelates two materials sharing the same scale     (0)
         floorBias 0..1, extra grime below `floorHeight`                (0.55)
         floorHeight metres over which the floor bias fades out         (1.6)
         inPlace  mutate instead of clone (see above)                   (false)

   MEASURED response (30/08/2026, grazing view of a lit floor, HDRI at 0.11 +
   one 0xffc890 lamp — i.e. this app's actual lighting). "local contrast" is
   mean |neighbour-pixel difference|, which ignores the lighting gradient and
   measures only the break-up grime adds:
       plain            luma 146.5   contrast 0.412   —
       strength 0.20    luma 144.0   contrast 0.462   +12%   barely there
       strength 0.34    luma 141.9   contrast 0.549   +33%   <- default
       strength 0.50    luma 138.8   contrast 0.691   +68%   dirty/derelict
       scale 0.5 m      luma 141.4   contrast 0.875  +112%   props, close-up
       scale 1.35 m     luma 139.3   contrast 0.553   +34%   <- default
       scale 3.0 m      luma 143.2   contrast 0.449    +9%   too broad to read
   Keep scale UNDER ~2.0 or the field is larger than anything you can see.
   The roughness half of the effect only shows against an environment map or a
   specular highlight — on a flat unlit test surface it measures as nothing. */
export function grime(material, opts = {}){
  const o = Object.assign({ scale:1.35, strength:0.34, seed:0, floorBias:0.55,
                            floorHeight:1.6, inPlace:false }, opts);
  const key = material.uuid+'|'+o.scale+'|'+o.strength+'|'+o.seed+'|'+o.floorBias+'|'+o.floorHeight;
  if(!o.inPlace && GRIME_CACHE.has(key)) return GRIME_CACHE.get(key);

  const mat = o.inPlace ? material : material.clone();
  const u = {
    gScale:  { value: 1/Math.max(0.01, o.scale) },
    gAmount: { value: o.strength },
    gSeed:   { value: o.seed },
    gLow:    { value: o.floorBias },
    gLowH:   { value: Math.max(0.01, o.floorHeight) },
  };
  const prev = mat.onBeforeCompile;
  mat.onBeforeCompile = (shader, rnd) => {
    if(prev && prev !== THREE.Material.prototype.onBeforeCompile) prev(shader, rnd);
    Object.assign(shader.uniforms, u);

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vGrimeP;')
      /* world position AFTER skinning/morph, with the instancing matrix folded
         in — figures are skinned and the books are instanced. */
      .replace('#include <project_vertex>', `#include <project_vertex>
        vec4 gWP = vec4( transformed, 1.0 );
        #ifdef USE_INSTANCING
          gWP = instanceMatrix * gWP;
        #endif
        vGrimeP = ( modelMatrix * gWP ).xyz;`);

    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
        varying vec3 vGrimeP;
        uniform float gScale, gAmount, gSeed, gLow, gLowH;
        float gHash( vec3 p ){
          p = fract( p * 0.3183099 + vec3( 0.1, 0.71, 0.42 ) );
          p *= 17.0;
          return fract( p.x * p.y * p.z * ( p.x + p.y + p.z ) );
        }
        float gNoise( vec3 x ){
          vec3 i = floor( x ), f = fract( x );
          f = f * f * ( 3.0 - 2.0 * f );
          return mix(
            mix( mix( gHash(i+vec3(0,0,0)), gHash(i+vec3(1,0,0)), f.x ),
                 mix( gHash(i+vec3(0,1,0)), gHash(i+vec3(1,1,0)), f.x ), f.y ),
            mix( mix( gHash(i+vec3(0,0,1)), gHash(i+vec3(1,0,1)), f.x ),
                 mix( gHash(i+vec3(0,1,1)), gHash(i+vec3(1,1,1)), f.x ), f.y ), f.z );
        }
        float gFbm( vec3 p ){
          return gNoise( p ) * 0.62 + gNoise( p * 2.17 + 19.3 ) * 0.26 + gNoise( p * 4.63 + 7.1 ) * 0.12;
        }
        float gField(){
          vec3 p = vGrimeP * gScale + gSeed * 13.77;
          return clamp( gFbm( p ) * 1.18 - 0.06, 0.0, 1.0 );
        }`)
      /* crevice / wear darkening — biased low, because that is where dirt is */
      .replace('#include <map_fragment>', `#include <map_fragment>
        float gF = gField();
        float gDirt = smoothstep( 0.34, 0.86, 1.0 - gF );
        gDirt *= mix( 0.5, 1.0, 1.0 - smoothstep( 0.0, gLowH, vGrimeP.y ) * ( 1.0 - gLow ) );
        diffuseColor.rgb *= 1.0 - gDirt * gAmount * 1.35;`)
      /* roughness swing — polished patches and dulled patches on one surface */
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
        roughnessFactor = clamp( roughnessFactor + ( gF - 0.5 ) * 1.7 * gAmount + gDirt * gAmount * 0.45, 0.045, 1.0 );`);
  };
  /* MANDATORY: without this two grime variants can be handed the same compiled
     program and one renders with the other's injected source. */
  mat.userData.__grimeKey = 'grime|'+o.scale+'|'+o.strength+'|'+o.seed+'|'+o.floorBias+'|'+o.floorHeight;
  mat.customProgramCacheKey = () => mat.userData.__grimeKey;
  mat.needsUpdate = true;
  sharpen(mat);
  if(!o.inPlace) GRIME_CACHE.set(key, mat);
  return mat;
}

/* ============================================================================
   lacquer — MeshStandardMaterial -> MeshPhysicalMaterial with a clearcoat
   ============================================================================ */
/* For desks, credenzas, laminate and any waxed/varnished timber. A clearcoat
   is a second specular lobe over the diffuse: it puts the lamps back into the
   wood as sharp highlights without washing the albedo out, which is the one
   thing that reads as "real furniture" in a dark room.

   Returns a NEW material (Standard cannot carry clearcoat). The caller must
   reassign it — see the report for the one-line world.traverse() swap that
   retargets everything currently using WOODD/WOODL.

   Passing an existing MeshPhysicalMaterial tunes it in place and returns it.

   opts: clearcoat          0..1 layer strength                    (0.55)
         clearcoatRoughness 0..1, >0.2 or the lamps become dots     (0.32)
         roughness          override the base roughness            (unchanged)
         reflectivity       base F0 for the diffuse lobe           (0.42)
         envMapIntensity    HDRI is at 0.11 globally; lift furniture (1.15)
         ior                                                       (1.5)
         sheen / sheenRoughness / sheenColor  optional cloth bloom  (off) */
export function lacquer(material, opts = {}){
  const o = Object.assign({ clearcoat:0.55, clearcoatRoughness:0.32, reflectivity:0.42,
                            envMapIntensity:1.15, ior:1.5 }, opts);
  let m = material;
  if(!material.isMeshPhysicalMaterial){
    m = new THREE.MeshPhysicalMaterial();
    THREE.MeshStandardMaterial.prototype.copy.call(m, material);   // all standard fields + maps, no undefined physical fields
    /* MeshStandardMaterial.copy() overwrites defines with { STANDARD:'' } and
       would strip PHYSICAL off the physical material. Put it back. */
    m.defines = { STANDARD:'', PHYSICAL:'' };
    m.userData = Object.assign({}, material.userData);
    carryShaderHooks(m, material);
  }
  m.clearcoat = o.clearcoat;
  m.clearcoatRoughness = o.clearcoatRoughness;
  m.reflectivity = o.reflectivity;
  m.envMapIntensity = o.envMapIntensity;
  m.ior = o.ior;
  if(o.roughness !== undefined) m.roughness = o.roughness;
  if(o.metalness !== undefined) m.metalness = o.metalness;
  if(o.sheen !== undefined){
    m.sheen = o.sheen;
    m.sheenRoughness = o.sheenRoughness !== undefined ? o.sheenRoughness : 0.6;
    if(o.sheenColor !== undefined) m.sheenColor = new THREE.Color(o.sheenColor);
  }
  if(o.anisotropy !== undefined){
    m.anisotropy = o.anisotropy;
    m.anisotropyRotation = o.anisotropyRotation || 0;
  }
  m.needsUpdate = true;
  return sharpen(m);
}

/* ============================================================================
   upgradeParquet — the herringbone floor
   ============================================================================ */
/* Wax on oak: a soft clearcoat so the pendants and lamps streak along the
   boards, plus a low sheen for the dust bloom that sits on any old parquet.

   ANISOTROPY CAVEAT: three's anisotropyRotation is a single global angle, but
   herringbone runs at +/-45 degrees, so any one rotation is wrong for half the
   boards. It is deliberately kept at 0.18 strength / 0 rotation — enough to
   smear the highlight along the tangent, low enough that the wrong half never
   reads as wrong. Do not raise it above ~0.25.

   Apply this PER ROOM. You cannot share one parquet material across the 12
   rooms: main.js builds each floor with pbrMat(slug, w/2.6, d/2.6, ...), so
   the plank tiling is baked into that room's own cloned textures via
   map.repeat. One shared material would retile every room to whichever
   room's repeat you kept. Twelve materials with identical defines still
   compile to ONE program, so the cost is 12 uniform blocks, not 12 shaders.
   The only way to get this to a single draw is to merge the parquet floor
   patches into one BufferGeometry with world-scaled UVs. */
export function upgradeParquet(material, opts = {}){
  return lacquer(material, Object.assign({
    clearcoat: 0.34,            // waxed, not varnished — a hard coat looks like vinyl
    clearcoatRoughness: 0.46,   // broad soft streak from the pendants
    roughness: 0.62,            // pbrMat ships these at .95; too dry for oiled oak
    reflectivity: 0.34,
    envMapIntensity: 1.0,
    sheen: 0.22,                // dust/wax bloom at grazing angles
    sheenRoughness: 0.72,
    sheenColor: 0xb59a76,       // warm — matches the 0xa6907a floor tint
    anisotropy: 0.18,
    anisotropyRotation: 0,
  }, opts));
}

/* ============================================================================
   glassMat — Loudfair lab partitions
   ============================================================================ */
/* PERF — what transmission actually costs, mechanically:
   A visible MeshPhysicalMaterial with transmission > 0 makes WebGLRenderer
   allocate and fill `_transmissionRenderTarget` — it re-renders the whole
   scene (opaque objects, all lights, all shadow lookups) into a half-resolution
   HDR target once per frame, BEFORE the main pass, and samples that target with
   a roughness-driven mip chain for the refraction. So:
     · +1 full scene render per frame (half res, no transparents)
     · +1 render target: innerWidth/2 x innerHeight/2, RGBA16F, mipmapped
     · transmissive meshes draw in the transparent pass, unsorted against
       each other, with depthWrite off
   That stacks ON TOP of this app's existing chain (RenderPass +
   UnrealBloomPass + OutputPass), which already renders the scene once and
   blurs it six times.

   MEASURED, not estimated — bench run 30/08/2026 in this repo's browser:
   200 shadow-casting meshes, 12 point lights, one 2048px shadow map, 4 glass
   panes, same renderer settings as main.js.
       cheap        :  106 draw calls   0.492 ms/frame
       transmission :  210 draw calls   1.600 ms/frame
   That is +104 draw calls — the ENTIRE opaque scene drawn a second time — and
   3.25x the GPU frame time. It scales with scene complexity, not with the
   number of panes, so the real floor (thousands of meshes, 4096px shadow map,
   12 lamps) pays proportionally more, and the ?fast path can't dodge it.

   And src/post.js's FocusPullPass (BokehPass) ALREADY re-renders the scene
   with a MeshDepthMaterial override, so the transmission target would be the
   THIRD full traversal of the scene graph per frame.

   VERDICT for this app: the Loudfair panes are 4 flat sheets seen edge-on at
   room scale. They gain almost nothing from real refraction. Ship `cheap`.
   Keep the transmission path for a hero shot or a screenshot render.

   `cheap: true` -> MeshPhysicalMaterial with NO transmission: opacity + a
   strong envMap response + low roughness. Zero extra passes, one extra
   transparent draw, visually ~90% of the way there at this scale. Defaults to
   true whenever the app is in ?fast mode.

   opts: cheap        force the fallback              (true under ?fast, else false)
         color                                        (0xa8aaa2 — matches main.js)
         opacity      cheap path only                 (0.16)
         roughness    frosting; 0 = optically clean   (0.06)
         thickness    transmission path only, metres  (0.06 — a pane, not a block)
         ior                                          (1.5)
         transmission                                 (0.92)
         attenuationColor / attenuationDistance       (warm, 2.4) */
export function glassMat(opts = {}){
  const fast = typeof location !== 'undefined' && location.search.includes('fast');
  const o = Object.assign({
    cheap: fast, color:0xa8aaa2, opacity:0.16, roughness:0.06, thickness:0.06,
    ior:1.5, transmission:0.92, attenuationColor:0xd8d2c2, attenuationDistance:2.4,
  }, opts);

  if(o.cheap){
    const m = new THREE.MeshPhysicalMaterial({
      color:o.color, roughness:o.roughness, metalness:0,
      transparent:true, opacity:o.opacity, depthWrite:false,
      envMapIntensity:2.6,          // the HDRI runs at 0.11 — glass has to claw it back
      reflectivity:0.5, ior:o.ior,
      clearcoat:0.5, clearcoatRoughness:0.06,   // the edge-on sheen that sells a pane
      side:THREE.DoubleSide,
    });
    m.userData.surfaceKind = 'glass-cheap';
    return sharpen(m);
  }
  const m = new THREE.MeshPhysicalMaterial({
    color:o.color, roughness:o.roughness, metalness:0,
    transmission:o.transmission, thickness:o.thickness, ior:o.ior,
    attenuationColor:new THREE.Color(o.attenuationColor), attenuationDistance:o.attenuationDistance,
    clearcoat:0.6, clearcoatRoughness:0.04,
    envMapIntensity:1.8, transparent:true, depthWrite:false, side:THREE.DoubleSide,
  });
  m.userData.surfaceKind = 'glass-transmission';
  return sharpen(m);
}

/* ============================================================================
   screenMat — monitors, room TVs, the Loudfair wireframe wall
   ============================================================================ */
/* MeshStandardMaterial + onBeforeCompile, deliberately NOT a ShaderMaterial:
   main.js's SCREENS loop drives `emissiveIntensity`, and three folds
   emissiveIntensity into the `emissive` uniform, so the shader mask below is
   purely MULTIPLICATIVE on totalEmissiveRadiance. That means
     SCREENS.push({ m: thisMaterial, base: 1.05 })
   keeps working exactly as it does today; the flicker and the scanlines
   compose instead of fighting.

   Gives you: fixed-pitch scanlines, one soft rolling refresh band, a vignette
   burned into the glass, and a slight darkening of the diffuse at the edges so
   the bezel corners fall off like real glass.

   The rolling band needs a clock — call tickScreens(t) once per frame from
   LOOP (see the report). Everything else works with no wiring at all.

   opts: material    tune an existing MeshStandardMaterial in place (e.g. tvUnit's
                     `smat`, which is built before its emissiveMap arrives)
         color / emissive / emissiveIntensity / map / emissiveMap / roughness
         lines       scanline count across the panel height   (170)
         scan        scanline depth 0..1                      (0.16)
         roll        rolling-band travel, screens/second      (0.09)
         rollWidth   band softness in UV                      (0.34)
         rollGain    band brightness gain                     (0.13)
         vignette    0..1 corner falloff on the glass         (0.34) */
export function screenMat(opts = {}){
  const o = Object.assign({
    color:0x05050a, emissive:0xffffff, emissiveIntensity:1.0, roughness:0.32, metalness:0.05,
    lines:170, scan:0.16, roll:0.09, rollWidth:0.34, rollGain:0.13, vignette:0.34,
  }, opts);

  const m = o.material || new THREE.MeshStandardMaterial({
    color:o.color, emissive:o.emissive, emissiveIntensity:o.emissiveIntensity,
    roughness:o.roughness, metalness:o.metalness,
  });
  if(o.map) m.map = o.map;
  if(o.emissiveMap) m.emissiveMap = o.emissiveMap;

  const u = {
    sTime:{ value:0 }, sLines:{ value:o.lines }, sScan:{ value:o.scan },
    sRoll:{ value:o.roll }, sRollW:{ value:Math.max(0.02, o.rollWidth) },
    sRollG:{ value:o.rollGain }, sVig:{ value:o.vignette },
  };
  m.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vScrUv;')
      .replace('#include <uv_vertex>', '#include <uv_vertex>\n  vScrUv = uv;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
        varying vec2 vScrUv;
        uniform float sTime, sLines, sScan, sRoll, sRollW, sRollG, sVig;
        float scrVig(){ return 1.0 - sVig * smoothstep( 0.26, 0.74, length( vScrUv - 0.5 ) ); }`)
      .replace('#include <map_fragment>', `#include <map_fragment>
        diffuseColor.rgb *= mix( 1.0, scrVig(), 0.65 );`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
        float sScanL = 0.5 + 0.5 * cos( vScrUv.y * sLines * 6.2831853 );
        float sBandP = fract( vScrUv.y - sTime * sRoll ) - 0.5;
        float sBand  = exp( - ( sBandP * sBandP ) / ( sRollW * sRollW ) );
        totalEmissiveRadiance *= ( 1.0 - sScan * sScanL ) * ( 1.0 + sRollG * sBand ) * scrVig();`);
  };
  m.customProgramCacheKey = () => 'screen|'+o.lines+'|'+o.scan+'|'+o.vignette;
  m.needsUpdate = true;
  m.userData.tick = t => { u.sTime.value = t; };     // per-material clock, if you'd rather not use tickScreens
  SCREEN_MATS.push(m);
  return sharpen(m);
}

/* One line in LOOP: tickScreens(t). Drives every screenMat's rolling band.
   Not in the requested export list — added because the band is dead without a
   clock and main.js's SCREENS loop only touches emissiveIntensity. */
export function tickScreens(t){
  for(let i=0;i<SCREEN_MATS.length;i++) SCREEN_MATS[i].userData.tick(t);
}

/* ============================================================================
   contactShadow / groundAll — grounding props and characters
   ============================================================================ */
/* SSAO is opt-in (?ssao) and shimmers on the animated figures, so nothing in
   this scene is grounded by ambient occlusion. A painted contact blob does the
   job for ~zero cost and never shimmers.

   ONE CircleGeometry and ONE MeshBasicMaterial for every blob in the scene,
   ever. contactShadow() allocates a Mesh and nothing else — no geometry, no
   material, no texture, on call 1 or call 500.

   Because the material is shared, `opacity` is a GLOBAL setting: the FIRST
   call (or initSurfaces) fixes it, later values are ignored. That is the
   trade the single-material constraint demands. When you need genuinely
   different per-blob darkness, use groundAll(objs, {batch:true}) — the
   instanced path carries per-instance alpha in instanceColor.r. */

function blobTex(){
  if(BLOB_TEX) return BLOB_TEX;
  const c = document.createElement('canvas'); c.width = c.height = 128;
  const g = c.getContext('2d');
  const r = g.createRadialGradient(64,64,0, 64,64,64);
  /* not a linear ramp: real contact occlusion is dense under the object and
     gone within about a third of its radius. */
  r.addColorStop(0.00,'#ffffff');
  r.addColorStop(0.28,'#e0e0e0');
  r.addColorStop(0.55,'#7a7a7a');
  r.addColorStop(0.80,'#242424');
  r.addColorStop(1.00,'#000000');
  g.fillStyle = r; g.fillRect(0,0,128,128);
  BLOB_TEX = new THREE.CanvasTexture(c);            // alphaMap reads .g — leave colorSpace default
  BLOB_TEX.anisotropy = MAX_ANISO;
  return BLOB_TEX;
}
function blobGeo(){
  if(!BLOB_GEO){ BLOB_GEO = new THREE.CircleGeometry(1, 32); BLOB_GEO.rotateX(-Math.PI/2); }
  return BLOB_GEO;                                   // unit radius, already lying flat
}
function blobMat(opacity){
  if(!BLOB_MAT){
    BLOB_MAT = new THREE.MeshBasicMaterial({
      color:0x08060a,                 // near-black with a hint of the room's cool shadow
      alphaMap: blobTex(),
      transparent:true,
      opacity: opacity !== undefined ? opacity : 0.46,
      depthWrite:false,
      toneMapped:false,               // ACES at exposure 1.5 would lift the blob off black
      polygonOffset:true, polygonOffsetFactor:-1, polygonOffsetUnits:-1,   // same trick main.js uses at l.1582
      side:THREE.FrontSide,
    });
  }
  return BLOB_MAT;
}

/* radius in world metres, opacity 0..1 (global — see above). */
export function contactShadow(radius = 0.5, opacity = 0.46){
  const m = new THREE.Mesh(blobGeo(), blobMat(opacity));
  m.scale.setScalar(radius);
  m.renderOrder = -1;                 // draw before the other transparents (glass, dust, labels)
  m.castShadow = m.receiveShadow = false;
  m.matrixAutoUpdate = true;
  m.userData.surfaceKind = 'contactShadow';
  return m;
}

/* the batch path: one InstancedMesh, one draw call, per-instance alpha */
function batchMat(){
  if(BATCH_MAT) return BATCH_MAT;
  BATCH_MAT = new THREE.MeshBasicMaterial({
    color:0x08060a, alphaMap:blobTex(), transparent:true, opacity:1,
    depthWrite:false, toneMapped:false,
    polygonOffset:true, polygonOffsetFactor:-1, polygonOffsetUnits:-1,
  });
  BATCH_MAT.onBeforeCompile = shader => {
    /* instanceColor rides in on vColor with NO material.vertexColors and no
       geometry `color` attribute: color_pars_vertex declares vColor under
       USE_INSTANCING_COLOR, and the FRAGMENT prefix defines USE_COLOR whenever
       parameters.instancingColor is set (three r170, WebGLProgram l.20199).
       Note USE_INSTANCING_COLOR itself is a VERTEX-ONLY define — guarding the
       fragment with it compiles clean and silently does nothing, so guard on
       USE_COLOR. color_fragment has already folded vColor into diffuseColor.rgb
       by this point, so restore the tint and take the alpha. */
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <alphamap_fragment>', `#include <alphamap_fragment>
        #ifdef USE_COLOR
          diffuseColor.rgb = diffuse;
          diffuseColor.a *= vColor.r;
        #endif`);
  };
  BATCH_MAT.customProgramCacheKey = () => 'cshadow-batch';
  return BATCH_MAT;
}

/* prop() adds its GLTF child inside a .then(), so Box3.setFromObject() on a
   freshly-returned prop group is EMPTY. Poll a few frames rather than
   grounding everything with a zero-radius blob. */
function fitWhenReady(obj, cb, tries = 300){
  obj.updateWorldMatrix(true, true);                 // Box3 only walks down, not up
  const box = new THREE.Box3().setFromObject(obj);
  if(!box.isEmpty()){
    const s = box.getSize(new THREE.Vector3());
    if(s.x > 1e-4 || s.z > 1e-4){ cb(box); return; }
  }
  if(tries > 0) requestAnimationFrame(() => fitWhenReady(obj, cb, tries-1));
}

/* Attach contact shadows under an array of objects, sized from their world
   bounding boxes. Handles async props (see fitWhenReady) and figure roots that
   carry a scale.

   objects : array of Object3D (prop() groups, rig roots, furniture groups)
   opts    : y        world height of the blob     (0.05 — clears floorPatch's
                                                    0.04 top; raise to 0.085 over
                                                    the corridor runner)
             spread   radius = spread * max(sizeX, sizeZ)       (0.95)
                      Do NOT drop this toward 0.6. A box 0.40 x 0.32 has a
                      half-extent of 0.20 on X and 0.256 on the diagonal, so
                      spread 0.62 -> radius 0.248 puts the ENTIRE blob inside
                      the object's own silhouette and it renders invisible.
                      At 0.95 the blob reaches the full footprint width, which
                      leaves a skirt about half the footprint wide — the part
                      you actually see. The alpha ramp is built for exactly
                      this: its dense core (0-28%) hides under the object and
                      the falloff lands on open floor.
             min/max  radius clamp in metres              (0.16 / 2.2)
             opacity  global for the mesh path, PER-BLOB for the batch path (0.46)
             batch    true -> ONE InstancedMesh for the lot. Static props only:
                      instances don't follow their object. Use it for plants,
                      chairs, shelves, boxes; NOT for the walking figures.
             parent   where the batch mesh is added (default: objects[0].parent)

   Returns { meshes:[] } for the per-object path, or { instanced: InstancedMesh }
   for the batch path. */
export function groundAll(objects, opts = {}){
  const o = Object.assign({ y:0.05, spread:0.95, min:0.16, max:2.2,
                            opacity:0.46, batch:false, parent:null }, opts);
  const list = (objects || []).filter(Boolean);
  const radiusOf = box => {
    const s = box.getSize(new THREE.Vector3());
    return Math.min(o.max, Math.max(o.min, Math.max(s.x, s.z) * o.spread));
  };

  if(!o.batch){
    /* The blob is created INSIDE the callback on purpose: Box3.setFromObject()
       ignores `visible` and walks every child, so a blob parented before the
       measurement would (a) inflate the footprint and (b) make a still-loading
       prop group look non-empty and fit against nothing but its own shadow. */
    const meshes = [];
    for(const obj of list){
      fitWhenReady(obj, box => {
        const blob = contactShadow(radiusOf(box), o.opacity);
        const c = box.getCenter(new THREE.Vector3()); c.y = o.y;
        obj.worldToLocal(c);                                // undo the parent's rotation/scale
        const ws = obj.getWorldScale(new THREE.Vector3());
        const k = Math.max(1e-4, (Math.abs(ws.x)+Math.abs(ws.z))/2);
        blob.position.copy(c);
        blob.scale.setScalar(radiusOf(box)/k);
        obj.add(blob);                                      // rides with the object
        meshes.push(blob);
      });
    }
    return { meshes };                                      // fills in as props resolve
  }

  /* batched: one draw call for every blob. Capacity is fixed at list.length;
     count grows as the async props resolve. */
  const n = Math.max(1, list.length);
  const im = new THREE.InstancedMesh(blobGeo(), batchMat(), n);
  im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n*3).fill(1), 3);
  im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  im.frustumCulled = false;          // the geometry bound is a unit circle; instances span the floor
  im.renderOrder = -1;
  im.castShadow = im.receiveShadow = false;
  im.count = 0;
  (o.parent || (list[0] && list[0].parent) || null)?.add(im);

  const d = new THREE.Object3D(), col = new THREE.Color();
  list.forEach(obj => fitWhenReady(obj, box => {
    const i = im.count++;
    const c = box.getCenter(new THREE.Vector3());
    d.position.set(c.x, o.y, c.z);
    d.scale.setScalar(radiusOf(box));
    d.rotation.set(0,0,0);
    d.updateMatrix(); im.setMatrixAt(i, d.matrix);
    im.setColorAt(i, col.setScalar(o.opacity));   // .r carries this blob's alpha
    im.instanceMatrix.needsUpdate = true;
    if(im.instanceColor) im.instanceColor.needsUpdate = true;
  }));
  return { instanced: im };
}
